import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { classesBotao, Selo, type CorSelo } from '../../componentes/base';
import { useCamposPersonalizados, valorExtra } from '../../componentes/formulario';
import { Erro } from '../../componentes/estados';
import { Drawer } from '../../componentes/sobreposicao';
import { SeloVerticais, useEmpresasDoCatalogo } from '../../componentes/verticais';
import { get } from '../../nucleo/api';
import { data, dataHora, dinheiro, email, nomeParceiro, nomePessoa, textoLivre } from '../../nucleo/formato';
import { rotuloEtapaPipeline, rotuloFunilPipeline, rotuloValor } from '../../nucleo/rotulos';
import type { NegociacaoCrm } from './ComercialFunil';
import { caminhoDaNegociacao } from './crm-caminhos';
import { rotuloStatusProposta } from './FichaPropostas';
import { iconeDoTipo } from './icones-atividade';

/**
 * Ajustes do CRM 27/10 (K18): "Sobre a negociação", o resumo rápido do ícone "i" do cartão e da linha da lista (o `openAbout`
 * do clone do RD), sem abrir a ficha. Lista de 30/10 (item 15): o resumo traz o que a pessoa precisa para não abrir o card:
 * valor, etapa e há quanto tempo, funil, status, responsável, empresa e contato principal, vertical, produtos, origem,
 * previsão, próximas atividades, última atividade, questionários, propostas e os campos personalizados preenchidos. O que o
 * cartão já tem vem dele; o resto, de UMA chamada (`/pipeline/crm/negociacoes/:id/resumo`), sem consulta por item. A última
 * anotação vem do histórico.
 */

const ENTIDADE = 'pipeline.oportunidade';

interface Mensagem {
  tipo: string;
  corpo: string;
  created_at: string;
}

/** A anotação mais recente do histórico (função pura, testada em `tests/crm-comercial-ajustes.test.ts`). */
export function ultimaAnotacao(mensagens: readonly Mensagem[]): Mensagem | null {
  let ultima: Mensagem | null = null;
  for (const m of mensagens) if (m.tipo === 'nota' && (!ultima || m.created_at > ultima.created_at)) ultima = m;
  return ultima;
}

export interface ResumoNegociacao {
  id: string;
  dias_na_etapa: number | null;
  origem: { origem: string | null; canal: string | null; fonte: string | null; midia: string | null; campanha: string | null };
  contato_principal: { parceiro_id: string; nome: string; email: string | null } | null;
  produtos: { total: number; itens: { descricao: string; quantidade: number }[] };
  atividades: {
    pendentes: number;
    atrasadas: number;
    interacoes: number;
    proximas: { id: string; titulo: string; tipo: string; data_hora: string; atrasada: boolean }[];
    ultima: { id: string; titulo: string; tipo: string; concluida_em: string } | null;
  };
  questionarios: { total: number; em_andamento: number; concluidos: number };
  propostas: { total: number; itens: { id: string; numero: number; titulo: string; total_centavos: number; moeda: string; estado: string; status_nome: string | null; status_cor: string | null }[] };
  extras: Record<string, unknown>;
}

const COR_STATUS: Record<NegociacaoCrm['status'], CorSelo> = { andamento: 'azul', vendido: 'verde', perdido: 'perigo', pausado: 'neutro' };
const COR_PROPOSTA: Record<string, CorSelo> = { criada: 'neutro', enviada: 'azul', aceita: 'verde', recusada: 'laranja' };
const MAX_CAMPOS = 6;

/** Texto "há N dias" da etapa (0 = hoje). */
export const diasNaEtapaTexto = (t: (k: string, o?: Record<string, unknown>) => string, dias: number | null) => (dias === null ? '' : dias === 0 ? t('crm.sobre.hoje') : t('crm.sobre.haDias', { count: dias }));

function Secao({ titulo, dado, children }: { titulo: string; dado: string; children: ReactNode }) {
  return (
    <section className="flex min-w-0 flex-col gap-2 border-t border-borda pt-3" data-sobre-secao={dado}>
      <h3 className="kicker">{titulo}</h3>
      {children}
    </section>
  );
}

function Campo({ rotulo, children, dado }: { rotulo: string; children: ReactNode; dado?: string }) {
  return (
    <div className="min-w-0" data-sobre-campo={dado}>
      <dt className="text-xs text-tinta-3">{rotulo}</dt>
      <dd className="mt-0.5 text-sm font-semibold break-words text-tinta-1">{children}</dd>
    </div>
  );
}


export function SobreNegociacao({ n, aoFechar }: { n: NegociacaoCrm; aoFechar: () => void }) {
  const { t } = useTranslation('pipeline');
  const resumo = useQuery({ queryKey: ['pipeline', 'resumo-negociacao', n.id], queryFn: () => get<ResumoNegociacao>(`/pipeline/crm/negociacoes/${n.id}/resumo`) });
  const historico = useQuery({ queryKey: ['chatter', ENTIDADE, n.id], queryFn: () => get<{ mensagens: Mensagem[] }>(`/chatter/${ENTIDADE}/${n.id}`) });
  const catalogo = useEmpresasDoCatalogo();
  const defs = useCamposPersonalizados('pipeline.oportunidade');
  const nada = t('crm.sobre.naoPreenchido');
  // RL301-VIS-B3: se o resumo (ou o histórico) falhar, o campo não fica em "..." para sempre; vale o "não preenchido" e, no lugar
  // das seções que dependem do resumo, o erro do núcleo com "tentar de novo".
  const carregando = resumo.isError ? nada : '...';
  const r = resumo.data;
  const nota = historico.data ? ultimaAnotacao(historico.data.mensagens) : null;
  const valor = n.itens ? n.valor_unico_centavos + n.valor_recorrente_centavos : n.valor_centavos;
  // RL301-VIS-B2: origem e canal passam pelo rótulo traduzido (como no Drawer); fonte, mídia e campanha seguem texto livre.
  const rotuloOrigem = (campo: 'origem' | 'canal', v: string | null) => (v ? rotuloValor(campo, v) : v);
  const origem = r ? [r.origem.fonte ?? n.utm_fonte, r.origem.campanha ?? n.utm_campanha, r.origem.midia, rotuloOrigem('origem', r.origem.origem), rotuloOrigem('canal', r.origem.canal)].filter((x, i, l): x is string => !!x && l.indexOf(x) === i) : [n.utm_fonte, n.utm_campanha].filter((x): x is string => !!x);
  // Campos personalizados preenchidos, do jeito da ficha (valor formatado; modo discreto), os primeiros.
  // Os valores crus só escolhem quais campos aparecem; a exibição passa por `valorExtra` (modo discreto), abaixo.
  const valoresExtras = r?.extras ?? {};
  const extras = defs
    .filter((d) => !d.visivel || d.visivel(valoresExtras))
    .map((d) => ({ d, v: valoresExtras[d.nome.slice(7)] }))
    .filter(({ v }) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && !v.length));
  const verticais = catalogo.data?.empresas ?? [];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('crm.comercial.sobre')}
      rodape={
        <Link to={caminhoDaNegociacao(n.id)} onClick={aoFechar} className={classesBotao('primario', 'md')} data-abrir-negociacao="">
          {t('crm.sobre.abrir')}
        </Link>
      }
    >
      <div className="flex min-w-0 flex-col gap-4" data-sobre-dados={n.id}>
        <header className="flex min-w-0 flex-col gap-1">
          <p className="text-base font-semibold break-words">{textoLivre(n.titulo, n.parceiro_nome)}</p>
          <p className="flex flex-wrap items-center gap-2">
            <span className="numero text-lg font-semibold" data-sobre-valor="">
              {dinheiro(valor, n.moeda)}
            </span>
            <Selo cor={COR_STATUS[n.status]}>{t(`crm.comercial.statusNeg.${n.status}`)}</Selo>
          </p>
        </header>
        <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Campo rotulo={t('crm.sobre.etapa')} dado="etapa">
            {rotuloEtapaPipeline(n.etapa_nome)}
            {r && r.dias_na_etapa !== null && <span className="block text-xs font-normal text-tinta-3">{diasNaEtapaTexto(t, r.dias_na_etapa)}</span>}
          </Campo>
          <Campo rotulo={t('crm.sobre.funil')} dado="funil">
            {n.funil_nome ? rotuloFunilPipeline(n.funil_nome) : nada}
          </Campo>
          <Campo rotulo={t('crm.sobre.responsavel')} dado="responsavel">
            {n.responsavel_nome ? nomePessoa(n.responsavel_nome) : t('crm.sobre.semResponsavel')}
          </Campo>
          <Campo rotulo={t('crm.sobre.empresa')} dado="empresa">
            {n.parceiro_nome ? nomeParceiro(n.parceiro_nome) : nada}
          </Campo>
          <Campo rotulo={t('crm.sobre.contatoPrincipal')} dado="contato">
            {!r ? carregando : r.contato_principal ? (
              <>
                {nomePessoa(r.contato_principal.nome)}
                {r.contato_principal.email && <span className="block text-xs font-normal break-all text-tinta-3">{email(r.contato_principal.email)}</span>}
              </>
            ) : (
              t('crm.sobre.semContato')
            )}
          </Campo>
          {verticais.length > 1 && (
            <Campo rotulo={t('crm.sobre.vertical')} dado="vertical">
              <SeloVerticais empresas={n.empresas ?? []} catalogo={verticais} />
            </Campo>
          )}
          <Campo rotulo={t('crm.sobre.produtos')} dado="produtos">
            {!r ? (
              carregando
            ) : r.produtos.total ? (
              <>
                {r.produtos.itens.map((p) => textoLivre(p.descricao, n.parceiro_nome)).join(', ')}
                {r.produtos.total > r.produtos.itens.length && <span className="font-normal text-tinta-3"> {t('crm.sobre.maisN', { count: r.produtos.total - r.produtos.itens.length })}</span>}
              </>
            ) : (
              t('crm.sobre.semProdutos')
            )}
          </Campo>
          <Campo rotulo={t('crm.sobre.origem')} dado="origem">
            {origem.length ? origem.map((o) => textoLivre(o)).join(' · ') : nada}
          </Campo>
          <Campo rotulo={t('crm.sobre.previsao')} dado="previsao">
            {n.previsao_fechamento ? data(`${n.previsao_fechamento}T12:00`) : nada}
          </Campo>
        </dl>

        {resumo.isError ? (
          <div data-sobre-erro="">
            <Erro erro={resumo.error} aoTentar={() => resumo.refetch()} />
          </div>
        ) : (
          <>
        <Secao titulo={t('crm.sobre.atividades')} dado="atividades">
          {!r ? (
            <p className="text-sm text-tinta-3">{carregando}</p>
          ) : (
            <>
              <p className="text-xs text-tinta-3" data-sobre-contagem-atividades="">
                {t('crm.sobre.nPendentes', { count: r.atividades.pendentes })}
                {r.atividades.atrasadas > 0 && <span className="text-laranja"> · {t('crm.sobre.nAtrasadas', { count: r.atividades.atrasadas })}</span>}
              </p>
              {r.atividades.proximas.length ? (
                <ul className="flex flex-col gap-1.5" data-sobre-proximas="">
                  {r.atividades.proximas.map((a) => {
                    const Icone = iconeDoTipo(a.tipo);
                    return (
                      <li key={a.id} className={`flex min-w-0 items-start gap-2 text-sm ${a.atrasada ? 'text-laranja' : ''}`} data-atrasada={a.atrasada || undefined}>
                        <Icone className="mt-0.5 size-4 shrink-0" aria-hidden />
                        <span className="min-w-0 flex-1 break-words">{textoLivre(a.titulo, n.parceiro_nome)}</span>
                        <span className="shrink-0 text-xs text-tinta-3">{dataHora(a.data_hora)}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-tinta-3">{t('crm.comercial.semTarefas')}</p>
              )}
              <p className="text-xs text-tinta-3" data-sobre-ultima="">
                {r.atividades.ultima
                  ? t('crm.sobre.ultimaAtividade', { tipo: t(`tipoAtividade.${r.atividades.ultima.tipo}`, { defaultValue: r.atividades.ultima.tipo }), titulo: textoLivre(r.atividades.ultima.titulo, n.parceiro_nome), quando: dataHora(r.atividades.ultima.concluida_em) })
                  : t('crm.sobre.semUltimaAtividade')}
                {' · '}
                {t('crm.sobre.nInteracoes', { count: r.atividades.interacoes })}
              </p>
            </>
          )}
        </Secao>

        <Secao titulo={t('crm.sobre.questionarios')} dado="questionarios">
          <p className="text-sm" data-sobre-questionarios="">
            {!r ? carregando : r.questionarios.total ? `${t('crm.questionarios.resumoTotal', { count: r.questionarios.total })} · ${t('crm.sobre.nEmAndamento', { count: r.questionarios.em_andamento })} · ${t('crm.sobre.nConcluidos', { count: r.questionarios.concluidos })}` : t('crm.sobre.semQuestionarios')}
          </p>
        </Secao>

        <Secao titulo={t('crm.sobre.propostas')} dado="propostas">
          {!r ? (
            <p className="text-sm text-tinta-3">{carregando}</p>
          ) : r.propostas.total ? (
            <ul className="flex flex-col gap-1.5" data-sobre-propostas="">
              {r.propostas.itens.map((p) => (
                <li key={p.id} className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="min-w-0 flex-1 break-words">{p.titulo ? textoLivre(p.titulo, n.parceiro_nome) : t('crm.sobre.propostaN', { n: p.numero })}</span>
                  <span className="numero text-xs text-tinta-2">{dinheiro(p.total_centavos, p.moeda)}</span>
                  <Selo cor={(p.status_cor as CorSelo | null) ?? COR_PROPOSTA[p.estado] ?? 'neutro'}>{rotuloStatusProposta(t as (k: string) => string, p.status_nome, p.estado as 'criada' | 'enviada' | 'aceita' | 'recusada')}</Selo>
                </li>
              ))}
              {r.propostas.total > r.propostas.itens.length && <li className="text-xs text-tinta-3">{t('crm.sobre.maisN', { count: r.propostas.total - r.propostas.itens.length })}</li>}
            </ul>
          ) : (
            <p className="text-sm text-tinta-3">{t('crm.sobre.semPropostas')}</p>
          )}
        </Secao>
          </>
        )}

        <Secao titulo={t('crm.sobre.dadosGerais')} dado="gerais">
          <dl className="grid min-w-0 grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
            <Campo rotulo={t('crm.sobre.ultimaAnotacao')}>{historico.data ? (nota ? textoLivre(nota.corpo, n.parceiro_nome) : t('crm.sobre.semAnotacao')) : historico.isError ? nada : '...'}</Campo>
            <Campo rotulo={t('crm.sobre.ultimoContato')}>{n.ultimo_contato ? dataHora(n.ultimo_contato) : nada}</Campo>
            <Campo rotulo={t('crm.sobre.criacao')}>{dataHora(n.created_at)}</Campo>
            <Campo rotulo={t('crm.sobre.identificador')}>
              <span className="numero text-xs">{n.id}</span>
            </Campo>
            {/* Lista de 30/10 (B), item 33: os campos personalizados entram no fim dos dados, sem área separada. */}
            {extras.slice(0, MAX_CAMPOS).map(({ d, v }) => (
              <Campo key={d.nome} rotulo={d.rotulo}>
                {valorExtra(d, v, n.parceiro_nome)}
              </Campo>
            ))}
          </dl>
          {extras.length > MAX_CAMPOS && <p className="text-xs text-tinta-3">{t('crm.sobre.maisN', { count: extras.length - MAX_CAMPOS })}</p>}
        </Secao>
      </div>
    </Drawer>
  );
}
